/**
 * API Service
 * Handles all HTTP requests to the M1NT backend API
 */

import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1'

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Request interceptor: logging + email session Bearer when no wallet header
api.interceptors.request.use(
  (config) => {
    console.log(`📤 ${config.method?.toUpperCase()} ${config.url}`)
    const headers = config.headers
    const xWallet =
      headers.get?.('X-Wallet-Address') ||
      headers.get?.('x-wallet-address') ||
      headers['X-Wallet-Address'] ||
      headers['x-wallet-address']
    const auth =
      headers.get?.('Authorization') ||
      (typeof headers.Authorization === 'string' ? headers.Authorization : '')
    const hasWalletHeader = xWallet && /^0x[a-fA-F0-9]{40}$/i.test(String(xWallet))
    const hasWalletBearer = /^Bearer 0x[a-fA-F0-9]{40}$/i.test(String(auth || ''))
    if (!hasWalletHeader && !hasWalletBearer && !auth) {
      try {
        const raw = localStorage.getItem('m1nt-auth-session')
        if (raw) {
          const s = JSON.parse(raw)
          if (s?.emailAuthToken) {
            headers.Authorization = `Bearer ${s.emailAuthToken}`
          }
        }
      } catch {
        // ignore
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log(`📥 ${response.status} ${response.config.url}`)
    return response
  },
  (error) => {
    console.error('❌ API Error:', error.response?.data || error.message)
    return Promise.reject(error)
  }
)

// ============================================================
// AUTH API (email login / registration / first-access flow)
// ============================================================

/** Body: { email, password? } */
export const loginWithEmail = (data) => api.post('/auth/login-email', data)
export const registerWithEmail = (data) => api.post('/auth/register-email', data)

/** First access step 1: { email } → { fromShopify, user, onboardingToken } */
export const startFirstAccess = (data) => api.post('/auth/first-access/start', data)

/** Header: Authorization Bearer onboardingToken. Body: { name, password, hasCryptoWallet, cryptoWallet? } */
export const completeEmailOnboarding = (onboardingToken, body) =>
  api.post('/auth/complete-email-onboarding', body, {
    headers: { Authorization: `Bearer ${onboardingToken}` },
  })

// ============================================================
// CARDS API
// ============================================================

export const getCard = (id) => api.get(`/cards/${id}`)

export const getCardsByOwner = (wallet) => api.get(`/cards/owner/${wallet}`)

/** Authenticated owner; uses email Bearer or wallet session from interceptors / headers */
export const getMyOwnerCards = (params) => api.get('/cards/owner/me', { params })

/** OmNova-only: list Lab users for test visibility selection. Requires wallet header. */
export const getLabUsersForVisibility = (wallet) =>
  api.get('/users/lab-visibility', wallet ? { headers: { 'X-Wallet-Address': wallet } } : {})

/** OmNova-only: mark card as test and set allowed Lab wallets. Requires wallet header. */
export const setCardTestVisibility = (cardId, wallet, data) =>
  api.patch(
    `/cards/${cardId}/test-visibility`,
    data,
    wallet ? { headers: { 'X-Wallet-Address': wallet } } : {}
  )

export const getCardStats = () => api.get('/cards/stats')

/** Optional: chain-of-custody timeline. Falls back to client-side build if 404. */
export const getCardTimeline = (id) => api.get(`/cards/${id}/timeline`)

export const getCardImages = (id) => api.get(`/cards/${id}/images`)

export const getBatchCardImages = (ids) =>
  api.get('/cards/batch-images', { params: { ids: ids.join(',') } })

/** Owner: pending burn requests for your cards only. Lab staff: all (same route, server filters). */
export const getBurnRequests = (status = 'PENDING') =>
  api.get('/burn-requests', { params: { status } })

// ============================================================
// ORDERS API
// ============================================================

export const createOrder = (data) => api.post('/orders', data)

export const confirmPayment = (id, data) => api.patch(`/orders/${id}/payment`, data)

export const getOrderStats = () => api.get('/orders/stats')

export const getMyOrders = (params) => api.get('/orders/my', { params })

export const confirmOwnerShipment = (id, data) => api.post(`/orders/${id}/confirm-shipment`, data)

/** Owner confirms they received the card (send wallet in header or as second arg) */
export const confirmDelivery = (id, wallet) =>
  api.post(`/orders/${id}/confirm-delivery`, {}, wallet ? withWallet(wallet) : {})

// ============================================================
// GRADERS API (requires wallet auth via header)
// ============================================================

export const getGraderOrders = (wallet, params = {}) =>
  api.get('/graders/orders', { params, ...withWallet(wallet) })

export const getGraderOrderById = (wallet, id) =>
  api.get(`/graders/orders/${id}`, withWallet(wallet))

export const confirmGraderReceipt = (wallet, id) =>
  api.post(`/graders/orders/${id}/confirm-receipt`, {}, withWallet(wallet))

export const saveGradingDraft = (wallet, id, data) =>
  api.patch(`/graders/orders/${id}/save-grading`, data, withWallet(wallet))

export const completeGrading = (wallet, id, data) =>
  api.post(`/graders/orders/${id}/complete-grading`, data, withWallet(wallet))

// ============================================================
// USERS API
// ============================================================

export const getUserByWallet = (wallet) => api.get(`/users/wallet/${wallet}`)

export const getMe = () => api.get('/users/me')

export const updateMyProfile = (data, wallet) =>
  api.patch('/users/me', data, wallet ? { headers: { 'x-wallet-address': wallet } } : {})

export const changeMyPassword = (data, wallet) =>
  api.post(
    '/users/me/change-password',
    data,
    wallet ? { headers: { 'x-wallet-address': wallet } } : {}
  )

// ============================================================
// MINT CREDITS API
// ============================================================

export const submitCardWithMintCredit = (formData) => {
  return api.post('/cards/request-quote', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 120000, // 2 minutes for file uploads
  })
}

export const getMyMintCredits = (wallet) =>
  api.get('/users/me/mint-credits', wallet ? withWallet(wallet) : {})

/** Request direct-upload signed policy for large video files. */
export const getSignedUploadUrl = (filename, contentType, folder = 'owner-uploads', wallet) =>
  api.post(
    '/upload/signed-url',
    { filename, contentType, folder },
    wallet ? withWallet(wallet) : {}
  )

/** Read Content-Type required by the signed POST policy (field names vary by SDK). */
const policyContentType = (fields) => {
  if (!fields || typeof fields !== 'object') return null
  return fields['Content-Type'] || fields['content-type'] || null
}

/** Best-effort parse of GCS XML error body for clearer UI messages. */
const gcsXmlMessage = (responseText) => {
  const m = String(responseText || '').match(/<Message>([^<]+)<\/Message>/i)
  return m ? m[1].trim() : null
}

/**
 * Ensure the file part's type matches the signed policy; mismatch often yields GCS 403.
 */
const fileMatchingPolicyType = (file, requiredType) => {
  if (!requiredType || file.type === requiredType) return file
  try {
    return new File([file], file.name, { type: requiredType, lastModified: file.lastModified })
  } catch {
    return file
  }
}

/** Upload directly to GCS using POST policy payload from /upload/signed-url. */
export const uploadFileToGCS = (signedPayload, file, onProgress) =>
  new Promise((resolve, reject) => {
    try {
      const upload = signedPayload?.upload || {}
      const fileMeta = signedPayload?.file || {}
      if (!upload?.url || !upload?.fields || !fileMeta?.gsUrl) {
        reject(new Error('Invalid signed upload payload'))
        return
      }
      const requiredType = policyContentType(upload.fields)
      const fileForUpload = fileMatchingPolicyType(file, requiredType)

      const formData = new FormData()
      Object.entries(upload.fields).forEach(([k, v]) => formData.append(k, v))
      formData.append('file', fileForUpload)

      const xhr = new XMLHttpRequest()
      xhr.upload.onprogress = (e) => {
        if (typeof onProgress === 'function' && e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100))
        }
      }
      xhr.onload = () => {
        if (xhr.status === 204 || xhr.status === 201 || xhr.status === 200) {
          resolve(fileMeta.gsUrl)
        } else {
          const detail = gcsXmlMessage(xhr.responseText)
          reject(
            new Error(
              detail
                ? `GCS upload failed: ${xhr.status} (${detail})`
                : `GCS upload failed: ${xhr.status}`
            )
          )
        }
      }
      xhr.onerror = () => reject(new Error('GCS upload network error'))
      xhr.open('POST', upload.url)
      xhr.send(formData)
    } catch (e) {
      reject(e)
    }
  })

// ============================================================
// NOTIFICATIONS API (requires wallet auth via header)
// ============================================================

const withWallet = (wallet) => (wallet ? { headers: { 'X-Wallet-Address': wallet } } : {})

// ============================================================
// CUSTODY TRANSFER (owner approves / rejects lab-initiated request)
// ============================================================

export const getMyPendingCustodyRequests = (wallet) =>
  api.get('/custody-transfer/requests/mine', wallet ? withWallet(wallet) : {})

export const rejectCustodyTransferRequest = (id, wallet) =>
  api.post(`/custody-transfer/requests/${id}/reject`, {}, withWallet(wallet))

export const approveCustodyTransferRequest = (id, wallet) =>
  api.post(`/custody-transfer/requests/${id}/approve`, {}, withWallet(wallet))

/** Prior owner: custody approved but NFT still needs safeTransferFrom on-chain */
export const getMyPendingOnChainCustody = (wallet) =>
  api.get('/custody-transfer/requests/on-chain-pending', wallet ? withWallet(wallet) : {})

export const submitCustodyOnChainComplete = (id, txHash, wallet) =>
  api.post(`/custody-transfer/requests/${id}/on-chain-complete`, { txHash }, withWallet(wallet))

/** Prior owner: cancel in-app custody handoff before on-chain tx; ownership reverts in M1NT. */
export const withdrawCustodyOnChainPending = (id, wallet) =>
  api.post(`/custody-transfer/requests/${id}/on-chain-withdraw`, {}, withWallet(wallet))

/** Owner: request on-chain delivery from Lab custody (email session or wallet). Body: { cardId, newOwnerWallet } */
export const createLabCustodyClaim = (body) =>
  api.post('/custody-transfer/requests/lab-custody-claim', body)

/** Owner: pending Lab custody claims submitted by the current user */
export const getMyLabCustodyClaims = () => api.get('/custody-transfer/requests/my-lab-claims')

export const withdrawLabCustodyClaim = (id) =>
  api.post(`/custody-transfer/requests/${id}/withdraw-lab-claim`, {})

export const getNotifications = (wallet, limit = 20) =>
  api.get('/notifications', { params: { limit }, ...(wallet ? withWallet(wallet) : {}) })

export const getUnreadCount = (wallet) =>
  api.get('/notifications/unread-count', wallet ? withWallet(wallet) : {})

export const markNotificationAsRead = (id, wallet) =>
  api.patch(`/notifications/${id}/read`, {}, wallet ? withWallet(wallet) : {})

export const markAllNotificationsAsRead = (wallet) =>
  api.patch('/notifications/mark-all-read', {}, wallet ? withWallet(wallet) : {})

export default api
